/**
 * gradient.ts — Section 4: "Gradient Descent (Learning via Derivatives)".
 *
 * Descends the cost surface y = x² by the update rule w ← w - η·f'(w). The
 * "Take One Step" button fires one update, animated over ~600ms with an
 * ease-out-cubic; the trail of past positions stays on the canvas as faint
 * dots. The slope / current parameter / error readouts and the direction line
 * update every frame.
 *
 * Faithful port of the draft's gradient-descent logic. The only structural
 * change is that mutable demo state (`w`, `history`, `animating`) lives on a
 * small state object rather than as closure lets — same semantics, easier to
 * reason about under strict TS.
 */

// Canvas maps x ∈ [-10, 10] horizontally and y ∈ [0, 100] vertically (matches
// the cost curve y = x² at its extremes).
const W_DOMAIN = 10; // x ranges -10..10
const Y_DOMAIN = 100; // y ranges 0..100

const LEARNING_RATE = 0.15; // η — the draft's value
const ANIM_MS = 600; // ease duration for one step
const START_W = 8; // initial parameter, far from the minimum at 0
const STOP_SLOPE = 0.01; // |f'(w)| below this → converged, button no-ops

interface State {
  w: number;
  history: number[];
  animating: boolean;
}

export function initGradient(): void {
  const canvas = document.getElementById('gdCanvas') as HTMLCanvasElement | null;
  const wDisplay = document.getElementById('gdWDisplay');
  const slopeDisplay = document.getElementById('gdSlopeDisplay');
  const errorDisplay = document.getElementById('gdErrorDisplay');
  const directionText = document.getElementById('gdDirectionText');
  const stepBtn = document.getElementById('gdStepBtn') as HTMLButtonElement | null;
  const resetBtn = document.getElementById('gdResetBtn') as HTMLButtonElement | null;
  if (!canvas || !wDisplay || !slopeDisplay || !errorDisplay || !directionText || !stepBtn || !resetBtn) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;

  const mapX = (x: number): number => ((x + W_DOMAIN) / (2 * W_DOMAIN)) * (W - 40) + 20;
  const mapY = (y: number): number => H - 20 - (y / Y_DOMAIN) * (H - 40);

  const state: State = { w: START_W, history: [START_W], animating: false };

  function drawAxes(): void {
    ctx!.beginPath();
    ctx!.strokeStyle = '#cbd5e1';
    ctx!.moveTo(20, H - 20);
    ctx!.lineTo(W - 20, H - 20); // x-axis
    ctx!.moveTo(W / 2, 20);
    ctx!.lineTo(W / 2, H - 20); // y-axis at x = 0
    ctx!.stroke();
  }

  function drawCostCurve(): void {
    ctx!.beginPath();
    ctx!.strokeStyle = '#94a3b8';
    ctx!.lineWidth = 3;
    for (let x = -W_DOMAIN; x <= W_DOMAIN; x += 0.2) {
      if (x === -W_DOMAIN) ctx!.moveTo(mapX(x), mapY(x * x));
      else ctx!.lineTo(mapX(x), mapY(x * x));
    }
    ctx!.stroke();
  }

  function draw(currentW: number): void {
    ctx!.clearRect(0, 0, W, H);
    drawAxes();
    drawCostCurve();

    // History trail — every past step as a faint dot.
    ctx!.fillStyle = '#fecdd3'; // rose-200
    for (const hw of state.history) {
      ctx!.beginPath();
      ctx!.arc(mapX(hw), mapY(hw * hw), 4, 0, Math.PI * 2);
      ctx!.fill();
    }

    // Current position.
    const currY = currentW * currentW;
    ctx!.fillStyle = '#e11d48'; // rose-600
    ctx!.beginPath();
    ctx!.arc(mapX(currentW), mapY(currY), 7, 0, Math.PI * 2);
    ctx!.fill();

    // Tangent line at the current point — the actual derivative (slope).
    const slope = 2 * currentW; // f'(w) for f(w) = w²
    const x1 = currentW - 3;
    const y1 = slope * (x1 - currentW) + currY;
    const x2 = currentW + 3;
    const y2 = slope * (x2 - currentW) + currY;

    ctx!.beginPath();
    ctx!.strokeStyle = '#be123c'; // rose-700
    ctx!.lineWidth = 2;
    ctx!.setLineDash([5, 5]);
    ctx!.moveTo(mapX(x1), mapY(y1));
    ctx!.lineTo(mapX(x2), mapY(y2));
    ctx!.stroke();
    ctx!.setLineDash([]);

    // Text readouts.
    wDisplay!.textContent = currentW.toFixed(2);
    slopeDisplay!.textContent = slope.toFixed(2);
    errorDisplay!.textContent = currY.toFixed(2);

    if (Math.abs(slope) < 0.1) {
      directionText!.textContent = 'Slope is ~0. We successfully hit rock bottom (minimum error)!';
      directionText!.className = 'text-sm mt-3 font-bold text-emerald-600';
    } else if (slope > 0) {
      directionText!.textContent = 'The slope is positive, so we step LEFT (subtract) to minimize error.';
      directionText!.className = 'text-sm mt-3 font-medium text-rose-800';
    } else {
      directionText!.textContent = 'The slope is negative, so we step RIGHT (add) to minimize error.';
      directionText!.className = 'text-sm mt-3 font-medium text-rose-800';
    }
  }

  function step(): void {
    if (state.animating || Math.abs(2 * state.w) < STOP_SLOPE) return;

    const slope = 2 * state.w;
    const newW = state.w - LEARNING_RATE * slope;

    // Ease-out-cubic interpolation from the current w to the updated w.
    state.animating = true;
    const startW = state.w;
    let startTime: number | null = null;

    function frame(t: number): void {
      if (startTime === null) startTime = t;
      let p = (t - startTime) / ANIM_MS;
      if (p > 1) p = 1;

      const ease = 1 - Math.pow(1 - p, 3);
      const tempW = startW + (newW - startW) * ease;
      draw(tempW);

      if (p < 1) {
        requestAnimationFrame(frame);
      } else {
        state.w = newW;
        state.history.push(state.w);
        state.animating = false;
        draw(state.w);
      }
    }
    requestAnimationFrame(frame);
  }

  function reset(): void {
    state.w = START_W;
    state.history = [START_W];
    draw(state.w);
  }

  stepBtn.addEventListener('click', step);
  resetBtn.addEventListener('click', reset);

  draw(state.w);
}
